// AI Insights sends the error rate already expressed as a percentage (`0.06`
// means `0.06%`), unlike the main dashboard which sends a fraction and multiplies
// by 100. We format to 2 decimals here without any `*100`, using a local
// `Intl.NumberFormat` rather than the dashboard's `numberFormatter` so this
// feature's precision rules stay independent.
const errorRatePercentageFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

// A nonzero rate below this value rounds to `0.00%` at 2 decimals. Rather than
// hide it behind a bare "0%" (reserved for a true zero), we render a floor
// indicator. Exported so the delta formatter (SCENARIO-07) reuses the same
// threshold instead of redefining it.
export const ERROR_RATE_ROUNDING_FLOOR = 0.005;

// A magnitude that rounds to `0.00` at 2 decimals is "too small to measure";
// both formatters branch on it, so the check lives in one shared predicate.
function isBelowRoundingFloor(magnitude: number): boolean {
  return magnitude < ERROR_RATE_ROUNDING_FLOOR;
}

const ERROR_RATE_FLOOR_LABEL = '< 0.01%';

export function formatErrorRatePercentage(value: number): string {
  if (value === 0) {
    return '0%';
  }

  if (isBelowRoundingFloor(value)) {
    return ERROR_RATE_FLOOR_LABEL;
  }

  return `${errorRatePercentageFormatter.format(value)}%`;
}

// Unsigned on purpose: the magnitude rounds to `0.00` either way, so a signed
// `"+< 0.01"` would pin a direction onto a value that means "too small to
// measure" — direction is conveyed by the arrow/colour, not this label.
const ERROR_RATE_DELTA_FLOOR_LABEL = '< 0.01';

// The delta uses the same 2-decimal formatter as the headline (SoT "same
// formatter" requirement, satisfied literally by reusing the instance above),
// stays absolute (percentage points), and owns its own sign so the caller no
// longer prefixes one.
export function formatErrorRateDelta(value: number): string {
  if (isBelowRoundingFloor(Math.abs(value))) {
    return ERROR_RATE_DELTA_FLOOR_LABEL;
  }

  const sign = value > 0 ? '+' : '-';

  return `${sign}${errorRatePercentageFormatter.format(Math.abs(value))}`;
}
